import React, { useEffect, useState, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useHospDocData } from "../../../lib/contexts/HospitalDoctorContext";
import {
  CacheWithExpiration,
  getFirstLastDateOfYear,
} from "../../../lib/utils/funcs";
import {
  getDoctorAttendanceAnalytics,
} from "../../../lib/apis/analytics";
import ChartError from "../../molecules/ChartError";
import ChartLoader from "../../molecules/ChartLoader";
import { DoctorAttendanceResponse } from "../../../lib/utils/types";

interface DoctorAttendanceGraphProps {
  doctor: {
    name: string;
    mappingId: string;
  };
  year: number;
}

function DoctorAttendanceGraph({ doctor, year }: DoctorAttendanceGraphProps) {
  const { hospitalID } = useHospDocData();
  const [attendanceData, setAttendanceData] =
    useState<DoctorAttendanceResponse>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleRetry = () => {
    fetchAttendanceData();
  };

  const fetchAttendanceData = async () => {
    if (!hospitalID || !doctor.mappingId) return;

    setError(null);

    // Set up delayed loading
    const loadingTimeout = setTimeout(() => {
      setIsLoading(true);
    }, 500);

    const { start_date, end_date } = getFirstLastDateOfYear(year);
    const cacheKey = `doctor-attendance-${hospitalID}-${doctor.mappingId}-${year}`;

    try {
      // Check cache first
      const cachedData = CacheWithExpiration.get(cacheKey);
      if (cachedData) {
        console.log("Using cached doctor attendance data: 🗄️");
        setAttendanceData(cachedData);
        clearTimeout(loadingTimeout);
        setIsLoading(false);
        return;
      }

      const data = await getDoctorAttendanceAnalytics({
        hospital_id: hospitalID,
        mapping_id: doctor.mappingId,
        start_date,
        end_date,
      });

      console.log("Fetched doctor attendance data: 🛜");

      // Cache the data with 60 minutes expiration
      CacheWithExpiration.set(cacheKey, data, 60);
      setAttendanceData(data);
    } catch (error) {
      setError("Failed to load attendance data. Please try again.");
      console.error("Failed to fetch doctor attendance data:", error);
    } finally {
      clearTimeout(loadingTimeout);
      setIsLoading(false);
    }
  };

  // Fetch data when component mounts or dependencies change
  useEffect(() => {
    if (hospitalID && doctor.mappingId && year) {
      fetchAttendanceData();
    }
  }, [hospitalID, doctor.mappingId, year]);

  console.log("Doctor Attendance Data: ✅", attendanceData);

  // Prepare the series with full data for tooltip accuracy, memoized
  const series = useMemo(() => [
    {
      name: "Attendance %",
      data: attendanceData.map((item) => ({
        x: item.month.split(" ")[0],
        y: item.percentage,
        attendedSessions: item.attendedSessions,
        scheduledSessions: item.scheduledSessions,
        fullMonth: item.month,
      })),
    },
  ], [attendanceData]);

  // Memoize chart options so they always match the current series and year
  const chartOptions = useMemo(() => ({
    chart: {
      height: 350,
      type: 'bar' as const,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 10,
        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val}%`,
      offsetY: -20,
      style: {
        fontSize: "12px",
        colors: ["#304758"],
      },
    },
    xaxis: {
      categories: series[0].data.map((item: any) => item.x),
      position: "top",
      axisBorder: { show: false },
      axisTicks: { show: false },
      crosshairs: {
        fill: {
          type: "gradient",
          gradient: {
            colorFrom: "#D8E3F0",
            colorTo: "#BED1E6",
            stops: [0, 100],
            opacityFrom: 0.4,
            opacityTo: 0.5,
          },
        },
      },
      tooltip: { enabled: true },
    },
    yaxis: {
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        show: false,
        formatter: (val: number) => `${val}%`,
      },
      max: 100,
      min: 0, 
    },
    // colors: ["#4AD991"],
    tooltip: {
      enabled: true,
      shared: false,
      intersect: false,
      y: {
        formatter: function (
          val: number,
          { series, seriesIndex, dataPointIndex, w }: { series: any; seriesIndex: number; dataPointIndex: number; w: any }
        ) {
          const point = w.config.series[seriesIndex].data[dataPointIndex];
          return `${val || 0}% (${point.attendedSessions || 0}/${point.scheduledSessions || 0} sessions)`;
        },
        title: {
          formatter: function (seriesName: string) {
            return "Attendance Rate";
          },
        },
      },
      x: {
        formatter: function (
          val: any,
          { series, seriesIndex, dataPointIndex, w }: { series: any; seriesIndex: number; dataPointIndex: number; w: any }
        ) {
          const point = w.config.series[seriesIndex].data[dataPointIndex];
          return point.fullMonth || val;
        },
      },
      marker: {
        show: true,
      },
      style: {
        fontSize: "12px",
        fontFamily: "inherit",
      },
    },
  }), [series, year]);

  return (
    <div className="bg-white p-4 rounded-lg shadow mt-6">

      {/* Loading State */}
      {isLoading && (
        <ChartLoader height={350} showLegend={false} containerClassName="" />
      )}

      {/* Error State */}
      {error && !isLoading && (
        <ChartError
          error={error}
          onRetry={handleRetry}
          containerClassName="border-t border-gray-100"
          height={350}
        />
      )}

      {/* Chart Content */}
      {!isLoading && !error && (
        <ReactApexChart
          options={chartOptions}
          series={series}
          type="bar"
          height={350}
        />
      )}

      {/* Footer */}
      <div className="mb-4">
        <p className="text-xs text-gray-500 text-center">
          Monthly Attendance % (Attended vs Scheduled Sessions)
        </p>
      </div>
    </div>
  );
}

export default DoctorAttendanceGraph;